import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Message, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import DesignModel from '../../../../../models/designModel';
import EventModel from '../../../../../models/eventModel';
import ListEvents, { ListRates } from './designRulesList';

import * as Sentry from '@sentry/browser';

function DeleteSubtitle({ designs }) {
    const { t } = useTranslation();

    return (
        <>
            <span>{t('ticket.ticket_design.designs.modale.delete.confirm', { count: designs?.length || 0 })}</span>
            <span>{t('ticket.ticket_design.designs.modale.cannot_undone')}</span>
        </>
    );
}

function DeleteComponent({ designs, setCanSubmit, organization, event, description }) {
    const { t } = useTranslation();

    const isDefault = React.useMemo(() => {
        if (!designs.length) return [];
        const isDefaultInDesigns = designs.some(design =>
            design.rules.some(r => r.organization_id === organization?.id && r.event_id === (event ? event?.id : null) && r.rate_id === null)
        );
        if (isDefaultInDesigns) setCanSubmit(false);
        return isDefaultInDesigns;
    }, [designs, organization, event]);

    return (
        <>
            <Form.Field>
                <Form.Label> {description} </Form.Label>
            </Form.Field>
            {isDefault && (
                <Message type="danger" title={t('ticket.ticket_design.designs.default.title')}>
                    {t('ticket.ticket_design.designs.default.warning')}
                </Message>
            )}
        </>
    );
}

function DuplicateComponent({ duplicateName = '', placeholderName = '', setDuplicateName = () => {}, setCanSubmit = () => {} }) {
    const { t } = useTranslation();

    return (
        <>
            <Form.Field>
                <Form.Label label={t('ticket.ticket_design.designs.modale.new_name')} />
                <Form.Input
                    required
                    name="duplicate_name"
                    placeholder={placeholderName}
                    value={duplicateName}
                    onChange={({ target: { value } }) => {
                        setCanSubmit(value?.length > 0 && value?.length < 60);
                        setDuplicateName(value);
                    }}
                />
            </Form.Field>
        </>
    );
}

export function DesignModale({ type = '', designs = [], setSelectedDesigns = () => {}, setShowModale = () => {}, organization, sync, event, level }) {
    const { t } = useTranslation();

    const placeholderName = React.useMemo(() => {
        const name = designs.length && designs[0].name;
        const placeholder = `${name} - ${t('ticket.common.copy')}`;
        return placeholder?.length < 60 ? placeholder : name;
    }, [designs]);
    const [duplicateName, setDuplicateName] = React.useState(placeholderName);
    const [canSubmit, setCanSubmit] = React.useState(true);

    const defaultTitle = t(`ticket.ticket_design.designs.modale.${type}.cta`, { name: designs.length && designs[0].name });

    const [events, eventsLoading] = EventModel.useApiModel(
        {
            organizationId: organization.id
        },
        [organization]
    );

    const toast = React.useContext(ToastContext);

    const _onClose = () => {
        setShowModale({ action: null, selected: [] });
    };

    const onDuplicate = React.useCallback(
        (design, value) => {
            if (!design) {
                console.error('design to duplicate not found');
                toast.error(t(`ticket.ticket_design.designs.modale.duplicate.error`));
                return;
            }
            const newDesign = new DesignModel(design);
            newDesign.name = duplicateName;
            newDesign.id = null;
            newDesign.rules = [];
            newDesign
                .save({ organizationId: organization.id, eventId: event?.id })
                .then(() => {
                    toast.success(t(`ticket.ticket_design.designs.modale.duplicate.success`));
                    sync();
                })
                .catch(e => {
                    console.error(e);
                    Sentry.captureException(e);
                    toast.error(t(`ticket.ticket_design.designs.modale.duplicate.error`));
                })
                .finally(() => {
                    setShowModale(false);
                });
        },
        [organization, sync, duplicateName, event]
    );

    const onDelete = React.useCallback(
        designsToDelete => {
            if (!designsToDelete) {
                console.error('design to delete not found');
                toast.error(t(`ticket.ticket_design.designs.modale.delete.error`));
                return;
            }
            DesignModel.destroy(designsToDelete, { organizationId: organization.id, eventId: event?.id })
                .then(() => {
                    toast.success(t(`ticket.ticket_design.designs.modale.delete.success`));
                    setSelectedDesigns([]);
                    sync();
                })
                .catch(e => {
                    console.error(e);
                    Sentry.captureException(e);
                    toast.error(t(`ticket.ticket_design.designs.modale.delete.error`));
                })
                .finally(() => {
                    setShowModale(false);
                });
        },
        [organization, setSelectedDesigns, sync, event]
    );

    const onSubmit = React.useCallback(() => {
        switch (type) {
            case 'delete':
                onDelete(designs);
                break;
            case 'duplicate':
                onDuplicate(designs[0], duplicateName);
                break;
            default:
                _onClose();
                break;
        }
    }, [type, designs, duplicateName, onDelete, onDuplicate, _onClose]);

    const getActionComponent = typeComponent => {
        switch (typeComponent) {
            case 'delete':
                return <DeleteComponent organization={organization} event={event} designs={designs} setCanSubmit={setCanSubmit} description={DeleteSubtitle({ designs })} />;
            case 'duplicate':
                return (
                    <DuplicateComponent
                        setCanSubmit={setCanSubmit}
                        duplicateName={duplicateName}
                        setDuplicateName={setDuplicateName}
                        placeholderName={placeholderName}
                        design={designs[0]}
                    />
                );
            case 'preview':
                return level === 'orga' ? (
                    <ListEvents organization={organization} design={designs[0]} events={events} eventsLoading={eventsLoading} />
                ) : (
                    <ListRates organization={organization} design={designs[0]} event={event} />
                );
            default:
                return <></>;
        }
    };

    return (
        <Modal
            size={'large'}
            allowExternalClosing={true}
            open={Boolean(type?.length > 0)}
            onClose={_onClose}
            title={type === 'preview' ? t(`ticket.ticket_design.designs.linked_${level === 'event' ? 'ticket' : 'event'}.title`) : defaultTitle}
            subtitle={null}
            customFooter={
                <div className={`wz-onboarding-modal-tour__footer`}>
                    <Button.Group>
                        {type !== 'preview' && <Button onClick={_onClose}>{t('ticket.common.action.cancel')}</Button>}
                        <Button disabled={!canSubmit} onClick={onSubmit} primary>
                            {t(type !== 'preview' ? 'ticket.common.action.confirm' : 'ticket.common.action.close')}
                        </Button>
                    </Button.Group>
                </div>
            }
        >
            {getActionComponent(type)}
        </Modal>
    );
}

DesignModale.propTypes = {
    type: PropTypes.string,
    designs: PropTypes.arrayOf(PropTypes.object),
    rules: PropTypes.arrayOf(PropTypes.object),
    setSelectedDesigns: PropTypes.func,
    setShowModale: PropTypes.func,
    organization: PropTypes.object,
    level: PropTypes.string,
    sync: PropTypes.func
};
